import { Field } from '../fields/Field';
import { GapFillFlag } from '../fieldtypes';
import { Field as FieldType } from '../fieldtypes/Field';
import { Message as MessageType } from '../fieldtypes/Message';
import type { IFIXParser } from '../IFIXParser';
import { LicenseManager } from '../licensemanager/LicenseManager';
import type { Message } from '../message/Message';

/**
 * Creates a Heartbeat message for the FIX session.
 * Heartbeat messages are used to maintain the connection and indicate that the session is still active.
 * Optionally includes a TestReqID if provided.
 *
 * @param {IFIXParser} parser - The FIX parser instance
 * @param {Field} [testReqId] - Optional TestReqID field to include in the heartbeat
 * @returns {Message} A FIX Heartbeat message
 * @throws {Error} If the license is invalid
 */
export const heartBeat = (parser: IFIXParser, testReqId?: Field): Message => {
    if (!LicenseManager.validateLicense()) {
        throw new Error(LicenseManager.ERROR_MESSAGE_NO_LICENSE);
    }
    const fields: Field[] = [
        new Field(FieldType.BeginString, parser.fixVersion),
        new Field(FieldType.MsgType, MessageType.Heartbeat),
        new Field(FieldType.MsgSeqNum, parser.getNextTargetMsgSeqNum()),
        new Field(FieldType.SenderCompID, parser.sender),
        new Field(FieldType.TargetCompID, parser.target),
        new Field(FieldType.SendingTime, parser.getTimestamp(new Date())),
    ];

    if (testReqId) {
        fields.push(testReqId);
    }

    return parser.createMessage(...fields);
};

/**
 * Creates a Sequence Reset message for the FIX session.
 * Used to reset the sequence numbers between two parties in a FIX session.
 * Can be used to fill gaps in message sequences or to reset the sequence number.
 *
 * @param {IFIXParser} parser - The FIX parser instance
 * @param {number} seqNo - The current sequence number
 * @param {number} newSeqNo - The new sequence number to set
 * @param {'Y' | 'N'} [gapFillFlag=GapFillFlag.GapFillMessage] - Flag indicating if this is a gap fill message
 * @returns {Message} A FIX Sequence Reset message
 * @throws {Error} If the license is invalid
 */
export const sequenceReset = (
    parser: IFIXParser,
    seqNo: number,
    newSeqNo: number,
    gapFillFlag: 'Y' | 'N' = GapFillFlag.GapFillMessage,
): Message => {
    if (!LicenseManager.validateLicense()) {
        throw new Error(LicenseManager.ERROR_MESSAGE_NO_LICENSE);
    }
    const fields: Field[] = [
        new Field(FieldType.BeginString, parser.fixVersion),
        new Field(FieldType.MsgType, MessageType.SequenceReset),
        new Field(FieldType.MsgSeqNum, seqNo),
        new Field(FieldType.SenderCompID, parser.sender),
        new Field(FieldType.TargetCompID, parser.target),
        new Field(FieldType.SendingTime, parser.getTimestamp(new Date())),
        new Field(FieldType.GapFillFlag, gapFillFlag),
        new Field(FieldType.NewSeqNo, newSeqNo + 1),
    ];

    return parser.createMessage(...fields);
};
